/*
  Product page behavior. Loaded as a module by sections/main-product.liquid.
  - <product-info>: variant changes (instant update, then Section Rendering refresh)
  - <product-form>: AJAX add to cart with real error messages; posts normally without JavaScript
  - <quantity-input>: 48px steppers
  - <media-gallery>: counter, dots, arrows, thumbnails, zoom, variant photo
  - <sticky-cta>: phone purchase bar while the main buttons are out of view (IntersectionObserver)
  Shared helpers (cart, WhatsApp, drawers, event bus) come from window.Spectre (assets/core.js).
*/
const Spectre = window.Spectre || {};

const cart = Spectre.cart;

/* ---------- <product-info> ---------- */
class ProductInfo extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.section;
    this.form = document.getElementById(this.dataset.formId);
    try {
      this.variants = JSON.parse(this.querySelector('[data-product-variants]')?.textContent || '[]');
    } catch (error) {
      this.variants = [];
    }
    this.renderCache = new Map();
    this.addEventListener('change', this.onChange);
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.onChange);
    this.abort?.abort();
  }

  get selectedOptions() {
    const positions = [...this.querySelectorAll('[data-option-position]')].map((fieldset) => {
      const checked = fieldset.querySelector('[data-option-input]:checked');
      return checked ? checked.value : null;
    });
    return positions;
  }

  onChange = (event) => {
    const input = event.target;
    if (input.matches('[data-property-input]')) {
      const fieldset = input.closest('[data-required-property]');
      const selected = fieldset?.querySelector('[data-option-selected]');
      if (selected) selected.textContent = input.value;
      fieldset?.querySelector('[data-property-error]')?.setAttribute('hidden', '');
      return;
    }
    if (!input.matches('[data-option-input]')) return;

    const fieldset = input.closest('[data-option-position]');
    const label = fieldset?.querySelector('[data-option-selected]');
    if (label) label.textContent = input.value;

    const options = this.selectedOptions;
    const variant = this.variants.find((candidate) => candidate.options.every((value, index) => value === options[index]));
    this.applyVariant(variant);
  };

  applyVariant(variant) {
    const strings = Spectre.strings || {};
    const idInput = this.form?.querySelector('[data-variant-input]');

    let label = strings.unavailable;
    let disabled = true;
    if (variant) {
      if (idInput) idInput.value = variant.id;
      label = variant.available ? strings.addToCart : strings.soldOut;
      disabled = !variant.available;
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    }

    document.querySelectorAll(`[data-add-button][form="${this.dataset.formId}"], #${this.dataset.formId} [data-add-button]`).forEach((button) => {
      button.disabled = disabled;
      const text = button.querySelector('[data-add-label]');
      if (text) text.textContent = label;
    });

    this.querySelectorAll('[data-region="sticky-variant"]').forEach((element) => {
      element.textContent = variant && variant.options.join(' / ') !== 'Default Title' ? variant.title : '';
    });
    if (variant) {
      this.querySelectorAll('[data-region="sticky-price"] .price__current').forEach((element) => {
        element.textContent = variant.price;
      });
    }

    const origin = window.location.origin;
    document.querySelectorAll('whatsapp-link[data-wa-product]').forEach((link) => {
      if (!variant) return;
      link.dataset.variantId = variant.id;
      link.setTokens?.({
        variant: variant.title === 'Default Title' ? '' : variant.title,
        price: variant.price,
        url: `${origin}${this.dataset.url}?variant=${variant.id}`,
      });
    });

    this.querySelector('[data-form-error]')?.setAttribute('hidden', '');
    Spectre.emit('variant:change', { sectionId: this.sectionId, variant });
    if (variant) this.refresh(variant);
  }

  /* Ask the server for the section as it renders for this variant, then swap the parts that depend on it. */
  async refresh(variant) {
    this.abort?.abort();
    this.abort = new AbortController();
    const key = String(variant.id);
    try {
      let html = this.renderCache.get(key);
      if (!html) {
        const url = `${this.dataset.url}?variant=${variant.id}&section_id=${this.sectionId}`;
        const response = await fetch(url, { signal: this.abort.signal });
        if (!response.ok) return;
        html = await response.text();
        this.renderCache.set(key, html);
      }
      const source = Spectre.parseHTML(html);
      const next = source.querySelector('product-info');
      if (!next) return;

      ['price', 'stock', 'sticky-price'].forEach((name) => {
        const fresh = next.querySelector(`[data-region="${name}"]`);
        this.querySelectorAll(`[data-region="${name}"]`).forEach((region) => {
          if (fresh && region.innerHTML !== fresh.innerHTML) region.innerHTML = fresh.innerHTML;
        });
      });

      next.querySelectorAll('[data-region^="note-"]').forEach((fresh) => {
        const region = this.querySelector(`[data-region="${fresh.dataset.region}"]`);
        if (region && region.innerHTML !== fresh.innerHTML) region.innerHTML = fresh.innerHTML;
      });

      /* Keep focus: update availability on the existing size buttons instead of replacing them. */
      next.querySelectorAll('[data-option-input]').forEach((freshInput) => {
        const input = this.querySelector(`#${CSS.escape(freshInput.id)}`);
        if (!input) return;
        input.toggleAttribute('data-unavailable', freshInput.hasAttribute('data-unavailable'));
        const freshLabel = next.querySelector(`label[for="${CSS.escape(freshInput.id)}"]`);
        const label = this.querySelector(`label[for="${CSS.escape(freshInput.id)}"]`);
        if (freshLabel && label && label.innerHTML !== freshLabel.innerHTML) {
          label.className = freshLabel.className;
          label.innerHTML = freshLabel.innerHTML;
        }
      });
    } catch (error) {
      if (error.name !== 'AbortError') console.warn('Spectre: variant refresh failed', error);
    }
  }
}
if (!customElements.get('product-info')) customElements.define('product-info', ProductInfo);

/* ---------- <product-form> ---------- */
class ProductForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    if (!this.form) return;
    /* form.id would return the <input name="id">, so read the attribute */
    this.formId = this.form.getAttribute('id');
    this.form.noValidate = true;
    this.error = this.querySelector('[data-form-error]');
    this.form.addEventListener('submit', this.onSubmit);
  }

  disconnectedCallback() {
    this.form?.removeEventListener('submit', this.onSubmit);
  }

  get buttons() {
    return [...document.querySelectorAll(`#${this.formId} [data-add-button], [data-add-button][form="${this.formId}"]`)];
  }

  showSuccess() {
    const notice = this.querySelector('[data-form-success]');
    if (!notice) return;
    notice.hidden = false;
    window.clearTimeout(this.successTimer);
    this.successTimer = window.setTimeout(() => {
      notice.hidden = true;
    }, 8000);
  }

  showError(message) {
    this.querySelector('[data-form-success]')?.setAttribute('hidden', '');
    if (!this.error) return;
    this.error.textContent = message;
    this.error.hidden = false;
    Spectre.announce(message);
  }

  /* Required sizes on one-variant products (line item property) */
  validate() {
    const groups = [...document.querySelectorAll('[data-required-property]')].filter((group) =>
      group.querySelector(`[data-property-input][form="${this.formId}"]`)
    );
    for (const group of groups) {
      const checked = group.querySelector('[data-property-input]:checked');
      const error = group.querySelector('[data-property-error]');
      if (!checked) {
        error?.removeAttribute('hidden');
        group.querySelector('[data-property-input]')?.focus();
        group.scrollIntoView({ block: 'center', behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
        Spectre.announce(error?.textContent?.trim());
        return false;
      }
      error?.setAttribute('hidden', '');
    }
    return true;
  }

  onSubmit = async (event) => {
    event.preventDefault();
    if (this.error) this.error.hidden = true;
    if (!this.validate()) return;

    const buttons = this.buttons;
    const labels = buttons.map((button) => button.querySelector('[data-add-label]'));
    const previous = labels.map((label) => label?.textContent);
    buttons.forEach((button, index) => {
      button.setAttribute('aria-busy', 'true');
      if (labels[index]) labels[index].textContent = Spectre.strings?.adding;
    });

    let added = false;
    try {
      await cart.add(new FormData(this.form), event.submitter?.closest('sticky-cta') ? 'sticky' : 'pdp');
      added = true;
      Spectre.announce(Spectre.strings?.added);
      if (!cart.sections().length) this.showSuccess();
    } catch (error) {
      this.showError(error.description || Spectre.strings?.addError);
      this.closest('[id^="BuyButtons-"]')?.scrollIntoView({ block: 'center' });
    } finally {
      buttons.forEach((button, index) => {
        button.removeAttribute('aria-busy');
        if (!labels[index]) return;
        labels[index].textContent = added ? Spectre.strings?.addedShort || previous[index] : previous[index];
      });
      if (added) {
        window.setTimeout(() => {
          labels.forEach((label, index) => {
            if (label && label.textContent === Spectre.strings?.addedShort) label.textContent = previous[index];
          });
        }, 2400);
      }
    }
  };
}
if (!customElements.get('product-form')) customElements.define('product-form', ProductForm);

/* ---------- <quantity-input> ---------- */
class QuantityInput extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input');
    this.addEventListener('click', this.onClick);
  }

  onClick = (event) => {
    const button = event.target.closest('[data-qty-step]');
    if (!button || !this.input) return;
    const min = Number(this.input.min) || 1;
    const current = Number(this.input.value) || min;
    const next = Math.max(min, current + Number(button.dataset.qtyStep));
    if (next === current) return;
    this.input.value = next;
    this.input.dispatchEvent(new Event('change', { bubbles: true }));
    document.querySelectorAll('whatsapp-link[data-wa-product]').forEach((link) => link.setTokens?.({ qty: String(next) }));
  };
}
if (!customElements.get('quantity-input')) customElements.define('quantity-input', QuantityInput);

document.addEventListener('change', (event) => {
  if (!event.target.matches('quantity-input input')) return;
  const value = Math.max(1, Number(event.target.value) || 1);
  if (String(value) !== event.target.value) event.target.value = value;
  document.querySelectorAll('whatsapp-link[data-wa-product]').forEach((link) => link.setTokens?.({ qty: String(value) }));
});

/* ---------- <media-gallery> ---------- */
class MediaGallery extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-gallery-track]');
    if (!this.track) return;
    this.slides = [...this.track.children];
    this.current = this.querySelector('[data-gallery-current]');
    this.dots = [...this.querySelectorAll('[data-gallery-dot]')];
    this.thumbs = [...this.querySelectorAll('[data-gallery-thumb]')];
    this.index = 0;

    this.querySelector('[data-gallery-prev]')?.addEventListener('click', () => this.go(this.index - 1));
    this.querySelector('[data-gallery-next]')?.addEventListener('click', () => this.go(this.index + 1));
    this.thumbs.forEach((thumb) => thumb.addEventListener('click', () => this.go(Number(thumb.dataset.galleryThumb))));
    this.addEventListener('click', this.onZoom);

    if (this.slides.length > 1 && 'IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(this.onIntersect, { root: this.track, threshold: 0.6 });
      this.slides.forEach((slide) => this.observer.observe(slide));
    }

    this.unsubscribe = Spectre.on?.('variant:change', ({ sectionId, variant }) => {
      if (sectionId !== this.dataset.section || !variant?.media) return;
      const target = this.slides.findIndex((slide) => slide.dataset.mediaId === String(variant.media));
      if (target > -1) this.go(target);
    });
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.unsubscribe?.();
  }

  onIntersect = (entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) this.setIndex(this.slides.indexOf(entry.target));
    });
  };

  setIndex(index) {
    if (index < 0) return;
    this.index = index;
    if (this.current) this.current.textContent = index + 1;
    this.dots.forEach((dot, position) => dot.classList.toggle('is-active', position === index));
    this.thumbs.forEach((thumb, position) => {
      thumb.classList.toggle('is-active', position === index);
      if (position === index) thumb.setAttribute('aria-current', 'true');
      else thumb.removeAttribute('aria-current');
    });
  }

  go(index) {
    if (!this.slides.length) return;
    const target = (index + this.slides.length) % this.slides.length;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const slide = this.slides[target];
    const trackBox = this.track.getBoundingClientRect();
    const slideBox = slide.getBoundingClientRect();
    const offset = slideBox.left - trackBox.left - (trackBox.width - slideBox.width) / 2;
    this.track.scrollBy({ left: offset, behavior: reduce ? 'auto' : 'smooth' });
    this.setIndex(target);
  }

  onZoom = (event) => {
    const button = event.target.closest('[data-zoom-media]');
    if (!button) return;
    const drawer = document.getElementById(`Zoom-${this.dataset.section}`);
    if (!drawer?.open) return;
    drawer.open(button);
    const item = drawer.querySelector(`[data-media-id="${button.dataset.zoomMedia}"]`);
    window.requestAnimationFrame(() => item?.scrollIntoView({ block: 'start' }));
  };
}
if (!customElements.get('media-gallery')) customElements.define('media-gallery', MediaGallery);

/* ---------- <sticky-cta> ---------- */
class StickyCta extends HTMLElement {
  connectedCallback() {
    this.target = document.getElementById(this.dataset.target);
    this.phone = window.matchMedia('(max-width: 989px)');
    if (!this.target || !('IntersectionObserver' in window)) return;
    this.observer = new IntersectionObserver(([entry]) => {
      this.targetVisible = entry.isIntersecting;
      this.update();
    });
    this.observer.observe(this.target);
    this.phone.addEventListener?.('change', this.update);
    this.offDrawerOpen = Spectre.on?.('drawer:open', () => this.update(true));
    this.offDrawerClose = Spectre.on?.('drawer:close', () => this.update());
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.phone?.removeEventListener?.('change', this.update);
    this.offDrawerOpen?.();
    this.offDrawerClose?.();
    this.toggle(false);
  }

  update = (forceHide) => {
    const show = forceHide !== true && this.phone.matches && this.targetVisible === false;
    this.toggle(show);
  };

  toggle(show) {
    if (this.visible === show) return;
    this.visible = show;
    this.classList.toggle('is-visible', show);
    document.body.classList.toggle('has-sticky-cta', show);
    Spectre.emit?.('sticky:change', { visible: show });
  }
}
if (!customElements.get('sticky-cta')) customElements.define('sticky-cta', StickyCta);

/* ---------- <product-recommendations> ---------- */
/*
  Loads when it nears the screen, or once the page is idle (so a jump past it still fills it).
  "auto" tries complementary products first, then related ones.
*/
class ProductRecommendations extends HTMLElement {
  connectedCallback() {
    if (this.hasAttribute('data-loaded') || !this.dataset.url) return;
    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) this.load();
        },
        { rootMargin: '600px 0px' }
      );
      this.observer.observe(this);
    }
    const idle = window.requestIdleCallback || ((callback) => window.setTimeout(callback, 2500));
    const schedule = () => idle(() => this.load(), { timeout: 5000 });
    if (document.readyState === 'complete') schedule();
    else window.addEventListener('load', schedule, { once: true });
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  async load() {
    if (this.loading || this.hasAttribute('data-loaded')) return;
    this.loading = true;
    this.observer?.disconnect();
    const intents = this.dataset.intent === 'auto' ? ['complementary', 'related'] : [this.dataset.intent || 'related'];
    for (const intent of intents) {
      try {
        const response = await fetch(`${this.dataset.url}&intent=${intent}`);
        if (!response.ok) continue;
        const fresh = Spectre.parseHTML(await response.text()).querySelector('product-recommendations');
        if (fresh && fresh.querySelector('.card')) {
          this.innerHTML = fresh.innerHTML;
          this.setAttribute('data-loaded', '');
          return;
        }
      } catch (error) {
        /* No recommendations: the section stays empty */
      }
    }
  }
}
if (!customElements.get('product-recommendations')) customElements.define('product-recommendations', ProductRecommendations);
